import React, { useEffect } from "react";
import { Caption } from "@bloomberg/media-ui-article";
import { useCombinedRefs } from "@bloomberg/media-ui-core";

import type { Image } from "~types/types";
import sizeImage, { DEFAULT_IMAGE } from "~utils/sizeImage";

import styles from "./styles.module.css";

const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

export interface LoopingVideoPlayerProps {
    url: string;
    thumbnail?: Image;
    imageWidth?: number;
}

// todo move this entire function into media-ui-av
const LoopingVideoPlayer = React.forwardRef<
    HTMLVideoElement,
    LoopingVideoPlayerProps
>(function LoopingVideoPlayerComponent(
    props: LoopingVideoPlayerProps,
    ref: React.ForwardedRef<HTMLVideoElement>
) {
    const { url, thumbnail, imageWidth } = props;

    const videoPlayerContainerEl = React.useRef<HTMLVideoElement | null>(null);

    // combine local ref with passed in forwardRef
    const videoRef = useCombinedRefs<HTMLVideoElement>(
        videoPlayerContainerEl,
        ref
    );

    let loopingVideoSrc = url ?? "";
    if (COLOR_ENV !== "production") {
        loopingVideoSrc =
            url?.replace("origin.staging.bloomberg.com", "www.bloomberg.com") ??
            "";
    }

    const poster = sizeImage(thumbnail?.baseUrl ?? DEFAULT_IMAGE, imageWidth);

    useEffect(() => {
        const loadVideo = async () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.Hls =
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.Hls ||
                (await import("hls.js/dist/hls.light.min.js")).default;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const Hls = window.Hls;

            if (!videoRef.current) {
                return;
            }

            // First check for native browser HLS support
            if (
                videoRef.current?.canPlayType("application/vnd.apple.mpegurl")
            ) {
                videoRef.current.src = loopingVideoSrc;

                // If no native HLS support, check if HLS.js is supported
            } else if (typeof Hls !== "undefined" && Hls.isSupported()) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const hls = new window.Hls();
                hls.loadSource(loopingVideoSrc);
                hls.attachMedia(videoRef.current);

                // may not need this...
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    videoRef?.current?.play();
                });
            } else {
                console.error("no hls supported for looping video");
            }
        };

        loadVideo();
    }, [videoRef.current]);

    return (
        <figure>
            {/* HTML5 HLS */}
            <video
                poster={poster ?? ""}
                width={imageWidth}
                height="auto"
                autoPlay
                playsInline
                muted
                loop
                ref={videoRef}
            />
        </figure>
    );
});

export interface LoopingVideoProps {
    url: string;
    credit?: string;
    thumbnail?: Image;
    showCaption?: boolean;
    imageWidth?: number;
}

export const LoopingVideo = React.forwardRef<
    HTMLVideoElement,
    LoopingVideoProps
>(function LoopingVideoComponent(
    props: LoopingVideoProps,
    ref: React.ForwardedRef<HTMLVideoElement>
) {
    const {
        url,
        credit,
        thumbnail,
        showCaption = true,
        imageWidth = 459,
    } = props;

    return (
        <div
            className={styles.LoopingVideo}
            data-component="lineup-looping-video"
        >
            <LoopingVideoPlayer
                url={url}
                thumbnail={thumbnail}
                imageWidth={imageWidth}
                ref={ref}
            />
            {showCaption && credit && (
                <Caption className={styles.caption} credit={credit} />
            )}
        </div>
    );
});
